import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { drawAvatarPreview, C } from './engine';
import {
  SKIN_OPTIONS, HAIR_OPTIONS, SHIRT_OPTIONS,
  makeDefaultAvatar, resolveAvatar, saveAvatar,
} from './avatar';

/** 色板按钮组 */
function SwatchGroup({ title, options, value, onChange }) {
  return (
    <div>
      <div className="pixel-town-field-label">{title}</div>
      <div className="flex flex-wrap gap-2 mt-2" role="group" aria-label={title}>
        {options.map((opt) => {
          const active = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onChange(opt.id)}
              title={opt.label}
              aria-label={`${title}：${opt.label}`}
              aria-pressed={active}
              className="pixel-town-swatch"
              style={{
                '--swatch': opt.main,
                '--swatch-dark': opt.dark || opt.light || opt.main,
              }}
            >
              {active && <Check size={15} strokeWidth={3} className="pixel-town-swatch__check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * 像素形象自定义弹窗
 * props:
 *   initialAvatar  当前形象（id 形式），为空时给默认值
 *   defaultName    名字默认值（当前用户名）
 *   allowCancel    是否显示取消按钮（首次进入为 false）
 *   onSave(avatar) 确认（内部已写 localStorage）
 *   onCancel       取消（仅 allowCancel 时）
 */
export default function AvatarCustomizer({ initialAvatar, defaultName, allowCancel = false, onSave, onCancel }) {
  const [avatar, setAvatar] = useState(() => initialAvatar || makeDefaultAvatar(defaultName));
  const [nameInput, setNameInput] = useState(() => (initialAvatar?.name ?? defaultName ?? '新同事'));
  const previewRef = useRef(null);

  // 选中配色变化时即时重绘像素小人
  useEffect(() => {
    drawAvatarPreview(previewRef.current, resolveAvatar(avatar), 6);
  }, [avatar]);

  const patch = (key) => (id) => setAvatar((prev) => ({ ...prev, [key]: id }));

  const handleConfirm = () => {
    const next = { ...avatar, name: (nameInput || '').trim().slice(0, 12) || defaultName || '新同事' };
    saveAvatar(next);
    onSave?.(next);
  };

  return (
    <div className="pixel-town-backdrop" role="dialog" aria-modal="true" aria-labelledby="pixel-avatar-title">
      <div className="pixel-town-modal">
        <div className="pixel-town-modal__header">
          <div>
            <h2 id="pixel-avatar-title" className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {allowCancel ? '更换小镇形象' : '创建你的小镇形象'}
            </h2>
            <p className="mt-1 text-xs" style={{ color: 'var(--color-text-muted)' }}>挑一套颜色，走进你的创作片场</p>
          </div>
        </div>

        <div className="pixel-town-modal__body">
          <div className="flex gap-5">
            <div className="pixel-town-avatar-preview" style={{ backgroundColor: C.wall, imageRendering: 'pixelated' }}>
              <canvas ref={previewRef} style={{ imageRendering: 'pixelated', display: 'block' }} />
            </div>

            <div className="flex-1 flex flex-col gap-4 min-w-0">
              <SwatchGroup title="肤色" options={SKIN_OPTIONS} value={avatar.skin} onChange={patch('skin')} />
              <SwatchGroup title="发色" options={HAIR_OPTIONS} value={avatar.hair} onChange={patch('hair')} />
              <SwatchGroup title="服装" options={SHIRT_OPTIONS} value={avatar.shirt} onChange={patch('shirt')} />
            </div>
          </div>

          <label className="mt-5 block">
            <span className="pixel-town-field-label">小镇昵称</span>
            <input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              maxLength={12}
              placeholder={defaultName || '新同事'}
              className="pixel-town-input"
            />
          </label>
        </div>

        <div className="pixel-town-modal__footer">
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>昵称最多 12 个字</span>
          <div className="flex gap-2">
            {allowCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="pixel-town-button"
              >
                取消
              </button>
            )}
            <button
              type="button"
              onClick={handleConfirm}
              className="pixel-town-button pixel-town-button--primary"
            >
              确认形象
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
